export type CodeGateResult = {
  ok: boolean;
  acceptedCode: bigint | null;
  reason: string;
  consecutiveCount: number;
  bestWindowCode: bigint | null;
  bestWindowVotes: number;
  windowSize: number;
};

export type CodeGateCandidateStats = {
  code: bigint;
  count: number;
  lastSeenFramesAgo: number;
  currentStreak: number;
  isAccepted: boolean;
  isBestWindow: boolean;
};

export type CodeGateSnapshot = {
  lastCode: bigint | null;
  consecutiveCount: number;
  requiredConsecutive: number;
  historyLength: number;
  windowSize: number;
  minWindowVotes: number;
  bestWindowCode: bigint | null;
  bestWindowVotes: number;
  candidates: CodeGateCandidateStats[];
  rejectedCount: number;
  expiredCount: number;
};

export class StableCodeGate {
  private history: bigint[] = [];
  private lastCode: bigint | null = null;
  private consecutiveCount = 0;
  private rejectedCount = 0;
  private expiredCount = 0;

  constructor(
    private readonly requiredConsecutive = 3,
    private readonly windowSize = 7,
    private readonly minWindowVotes = 4
  ) {}

  clear():void
  {
    this.lastCode = null;
    this.history = [];
    this.consecutiveCount = 0;
    this.rejectedCount = 0;
    this.expiredCount = 0;
  }
  push(code: bigint): CodeGateResult {
    if (this.lastCode === code) {
      this.consecutiveCount++;
    } else {
      this.lastCode = code;
      this.consecutiveCount = 1;
    }

    this.history.push(code);
    if (this.history.length > this.windowSize) {
      this.history.shift();
      this.expiredCount++;
    }

    const counts = new Map<bigint, number>();
    for (const c of this.history) {
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }

    let bestWindowCode: bigint | null = null;
    let bestWindowVotes = 0;

    for (const [c, count] of counts.entries()) {
      if (count > bestWindowVotes) {
        bestWindowCode = c;
        bestWindowVotes = count;
      }
    }

    if (this.consecutiveCount >= this.requiredConsecutive) {
      return {
        ok: true,
        acceptedCode: code,
        reason: `consecutive x${this.consecutiveCount}`,
        consecutiveCount: this.consecutiveCount,
        bestWindowCode,
        bestWindowVotes,
        windowSize: this.windowSize,
      };
    }

    if (bestWindowCode !== null && bestWindowVotes >= this.minWindowVotes) {
      return {
        ok: true,
        acceptedCode: bestWindowCode,
        reason: `window vote ${bestWindowVotes}/${this.history.length}`,
        consecutiveCount: this.consecutiveCount,
        bestWindowCode,
        bestWindowVotes,
        windowSize: this.windowSize,
      };
    }

    this.rejectedCount++;
    return {
      ok: false,
      acceptedCode: null,
      reason: "not stable yet",
      consecutiveCount: this.consecutiveCount,
      bestWindowCode,
      bestWindowVotes,
      windowSize: this.windowSize,
    };
  }

  reset(): void {
    this.history = [];
    this.lastCode = null;
    this.consecutiveCount = 0;
    this.rejectedCount = 0;
    this.expiredCount = 0;
  }

  getHistory(): bigint[] {
    return [...this.history];
  }

  /**
   * Read-only snapshot of the current gate state for diagnostics. Does not
   * mutate history, lastCode, or consecutiveCount.
   */
  snapshot(acceptedCode: bigint | null = null): CodeGateSnapshot {
    const counts = new Map<bigint, number>();
    for (const c of this.history) {
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    let bestWindowCode: bigint | null = null;
    let bestWindowVotes = 0;
    for (const [c, count] of counts.entries()) {
      if (count > bestWindowVotes) {
        bestWindowCode = c;
        bestWindowVotes = count;
      }
    }

    const currentStreaks = new Map<bigint, number>();
    let streakCode: bigint | null = null;
    let streakCount = 0;
    for (const code of this.history) {
      if (streakCode === code) {
        streakCount++;
      } else {
        streakCode = code;
        streakCount = 1;
      }
      currentStreaks.set(code, streakCount);
    }

    const candidates = Array.from(counts.entries())
      .map(([code, count]) => {
        const lastIndex = this.history.lastIndexOf(code);
        return {
          code,
          count,
          lastSeenFramesAgo:
            lastIndex >= 0 ? this.history.length - 1 - lastIndex : this.history.length,
          currentStreak: currentStreaks.get(code) ?? 0,
          isAccepted: acceptedCode !== null && code === acceptedCode,
          isBestWindow: bestWindowCode !== null && code === bestWindowCode,
        };
      })
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        if (a.lastSeenFramesAgo !== b.lastSeenFramesAgo) {
          return a.lastSeenFramesAgo - b.lastSeenFramesAgo;
        }
        return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
      });

    return {
      lastCode: this.lastCode,
      consecutiveCount: this.consecutiveCount,
      requiredConsecutive: this.requiredConsecutive,
      historyLength: this.history.length,
      windowSize: this.windowSize,
      minWindowVotes: this.minWindowVotes,
      bestWindowCode,
      bestWindowVotes,
      candidates,
      rejectedCount: this.rejectedCount,
      expiredCount: this.expiredCount,
    };
  }
}
